import { useEffect, useMemo, useRef, useState } from 'react';

import { type ListenerOptions, type MessageHandler, type SendMessageOptions } from './webview-bridge.type';
import { WebviewBridgeContext } from './webview-bridge-context';
import { WebviewBridgeContextValue } from './webview-bridge-context.type';
import type { WebviewBridgeProviderProps } from './webview-bridge-provider.type';
import { WebviewBridgeService } from './webview-bridge-service';

/**
 * Webview Bridge Provider
 *
 * Provides webview bridge functionality to all child components.
 * Automatically initializes and cleans up the bridge service.
 */
export const WebviewBridgeProvider = ({ children, config }: WebviewBridgeProviderProps) => {
  const serviceRef = useRef<WebviewBridgeService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initStartedRef = useRef(false);
  // Freeze config on first mount. Bridge config is semantically static
  // (allowedOrigin, messagePrefix); rebuilding the service on every config
  // object-identity change would drop listeners and reject pending requests.
  const configRef = useRef(config);

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new WebviewBridgeService(configRef.current);
    }

    const service = serviceRef.current;

    if (!initStartedRef.current) {
      service.initialize();
      initStartedRef.current = true;
      setIsInitialized(true);
    }

    return () => {
      serviceRef.current?.destroy();
      serviceRef.current = null;
      initStartedRef.current = false;
      setIsInitialized(false);
    };
  }, []);

  const contextValue = useMemo<WebviewBridgeContextValue>(() => {
    const service = serviceRef.current;
    if (!service || !isInitialized) {
      const notInitializedError = () => new Error('Webview bridge service not initialized');
      return {
        send: () => Promise.reject(notInitializedError()),
        on: () => () => {
          // no-op
        },
        once: () => Promise.reject(notInitializedError()),
        off: () => {
          // no-op
        },
        removeAllListeners: () => {
          // no-op
        },
        isInitialized: false,
      };
    }

    return {
      send: <T = Record<string, unknown>, R = Record<string, unknown>>(
        type: string,
        data: T,
        options?: SendMessageOptions
      ) => service.send<T, R>(type, data, options),
      on: <T = Record<string, unknown>,>(type: string, handler: MessageHandler<T>, options?: ListenerOptions) =>
        service.on<T>(type, handler, options),
      once: <T = Record<string, unknown>,>(type: string, handler?: MessageHandler<T>) => service.once<T>(type, handler),
      off: (type: string) => service.off(type),
      removeAllListeners: () => service.removeAllListeners(),
      isInitialized: true,
    };
  }, [isInitialized]);

  return <WebviewBridgeContext.Provider value={contextValue}>{children}</WebviewBridgeContext.Provider>;
};
